import { toApiUrl } from '../../utils/apiBase';
import React, { useContext, useEffect, useState } from 'react';
import firestore from '../../firestore';
import { GlobalContext } from '../../context/GlobalContext';
import Input from '../../components/Input';
import { Button } from '../../shadcn/Button';
import Notice from '../../components/Notice';
import Stepper from '../../components/Stepper';
import classNames from 'classnames';
import { unsafeAPI } from '../../hooks/api';
import TwoFactorAuth from '../../components/2FA';
import LivenessDetection from '../liveness-detection';
import { QRCodeSVG } from 'qrcode.react';
import copy from 'copy-to-clipboard';
import { AlertTriangle, ArrowLeft, Camera, CircleCheck, Copy, KeyRound, PhoneCall, QrCode, Search } from 'lucide-react';
import { validate_phone_number } from '../../utils/validate';
import { ContainerSpinner } from '../../components/Spinner';
import { format_identification_with_prefix } from '../../utils/formatters';

const CambiarTelefono = () => {
	const { userData, CountrySettings } = useContext(GlobalContext);
	const uidPrefix = CountrySettings?.uid_prefix || '';

	// Pages: 1 (buscar), 2 (liveness), 3 (cambiar teléfono), 4 (éxito)
	const [page, setPage] = useState<number>(1);
	const [success, setSuccess] = useState(false);
	const steps = [
		{ label: userData?.country === 'peru' ? 'DNI' : 'Cédula', pages: [1] },
		{ label: 'Verificación Biométrica', pages: [2] },
		{ label: 'Cambiar Teléfono', pages: [3] },
		{ label: 'Teléfono Actualizado', pages: [4] }
	];

	// Buscar usuario
	const [cedula, setCedula] = useState('');
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchError, setSearchError] = useState('');
	const [userIdentificationNumber, setUserIdentificationNumber] = useState('');
	const [userCountry, setUserCountry] = useState('');
	const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');
	const cedulaLabel = userData?.country === 'peru' ? 'DNI' : 'Cédula';

	const handleSearchUser = async (e?: React.FormEvent) => {
		e?.preventDefault?.();
		setSearchError('');
		if (!cedula) {
			setSearchError(`Por favor introduzca ${userData?.country === 'peru' ? 'el DNI' : 'la cédula'}`);
			return;
		}
		if (!/^[0-9]+$/.test(cedula)) {
			setSearchError(`Por favor ingrese un número de ${userData?.country === 'peru' ? 'DNI' : 'cédula'} válido (sólo números).`);
			return;
		}
		setSearchLoading(true);
		try {
			const usersCollection = firestore.collection('Users');
			const queryUser = usersCollection
				.where('identification_number', '==', format_identification_with_prefix(cedula, uidPrefix))
				.limit(1);
			const snap = await queryUser.get();
			const data = snap.docs?.[0]?.data();
			if (!data) {
				setSearchError(`No se encontró un usuario con ${userData?.country === 'peru' ? 'DNI' : 'cédula'} ${cedula}.`);
				return;
			}
			if (!data?.country) {
				setSearchError('No se pudo obtener el país del usuario.');
				return;
			}
			setUserIdentificationNumber(data.identification_number);
			setUserCountry(data.country);
			setCurrentPhoneNumber(data.phone_number || '');
			setPage(2);
			checkCameraAvailability();
		} catch {
			setSearchError('Ocurrió un problema, por favor intente nuevamente.');
		} finally {
			setSearchLoading(false);
		}
	};

	// Liveness (siempre habilitado)
	type LivenessState = { sessionId: string; documentId: string; qrUrl: string; results?: any } | null;
	const [liveness, setLiveness] = useState<LivenessState>(null);
	const [livenessMode, setLivenessMode] = useState<'idle' | 'embedded' | 'qr'>('idle');
	const [formError, setFormError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [hideStepper, setHideStepper] = useState(false);
	const [isCopySuccess, setIsCopySuccess] = useState(false);

	// Liveness rate limiting
	const [livenessRateLimitTimer, setLivenessRateLimitTimer] = useState(0);
	const [isLivenessRateLimited, setIsLivenessRateLimited] = useState(false);
	useEffect(() => {
		let t: any;
		if (livenessRateLimitTimer > 0) {
			setIsLivenessRateLimited(true);
			t = setInterval(() => setLivenessRateLimitTimer((s) => (s <= 1 ? 0 : s - 1)), 1000);
		} else {
			setIsLivenessRateLimited(false);
		}
		return () => t && clearInterval(t);
	}, [livenessRateLimitTimer]);

	const initializeLiveness = async () => {
		// Check rate limit
		if (isLivenessRateLimited) {
			setFormError(`Por favor espere ${livenessRateLimitTimer} segundos antes de intentar nuevamente.`);
			return false;
		}

		setIsLoading(true);
		setFormError('');
		try {
			const response = await unsafeAPI(
				'GET',
				`https://api.krece.app/authentication/liveness-detection-session/?user_identification_number=${encodeURIComponent(userIdentificationNumber)}`,
				null,
				null,
				null,
				false
			);

			if (response?.success && response?.data?.session_id && response?.data?.document_id) {
				const qrUrl = `${window.location.origin}/liveness-detection?liveness_session_id=${response.data.session_id}&document_id=${response.data.document_id}&user_identification_number=${encodeURIComponent(userIdentificationNumber)}`;

				setLiveness({
					sessionId: response.data.session_id,
					documentId: response.data.document_id,
					qrUrl
				});

				setLivenessRateLimitTimer(30);

				return true;
			} else {
				throw new Error();
			}
		} catch (error) {
			console.error('Error initializing liveness:', error);
			setLiveness(null);
			setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const [hasCameraAvailable, setHasCameraAvailable] = useState(false);
	const [isCameraFunctional, setIsCameraFunctional] = useState(false);
	const [cameraTestInProgress, setCameraTestInProgress] = useState(false);
	const checkCameraAvailability = async () => {
		try {
			if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
				const devices = await navigator.mediaDevices.enumerateDevices();
				const hasCamera = devices.some((device) => device.kind === 'videoinput');
				setHasCameraAvailable(hasCamera);
			} else {
				setHasCameraAvailable(false);
			}
		} catch (error) {
			console.error('Error checking camera availability:', error);
			setHasCameraAvailable(false);
		}
	};
	useEffect(() => {
		checkCameraAvailability();
	}, []);

	const testCameraFunctionality = async () => {
		if (!hasCameraAvailable || cameraTestInProgress) return false;

		setCameraTestInProgress(true);
		let stream = null;

		try {
			// Request camera permissions and test actual stream with 15s timeout
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(() => reject(new Error('Camera test timeout')), 15000);
			});

			const cameraPromise = navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 640 },
					height: { ideal: 480 }
				}
			});

			stream = (await Promise.race([cameraPromise, timeoutPromise])) as MediaStream;

			// Check if stream has video tracks and they're active
			const videoTracks = stream.getVideoTracks();
			const isFunctional = videoTracks.length > 0 && videoTracks[0].readyState === 'live';

			setIsCameraFunctional(isFunctional);
			return isFunctional;
		} catch (error) {
			setIsCameraFunctional(false);
		} finally {
			// Clean up stream
			if (stream) {
				stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
			}
			setCameraTestInProgress(false);
		}
	};

	useEffect(() => {
		if (success) {
			setPage(4);
		} 
	}, [success]);

	useEffect(() => {
		if (page === 2 && hasCameraAvailable) testCameraFunctionality();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, hasCameraAvailable]);

	// Escuchar resultados de liveness
	useEffect(() => {
		if (!liveness?.documentId) return;
		let unsub: any = null;
		try {
			unsub = firestore
				.collection('LivenessSessions')
				.doc(liveness.documentId)
				.onSnapshot((snap) => {
					try {
						if (snap?.exists) {
							const data = snap.data();

						    const isSuccess = data?.valid_user === true && data?.user_authenticated === true && data?.photo_id_url;

							// Remove processing flag when verification is complete (success or failure)
							if (data && 'processing' in data && isSuccess) {
								delete data.processing;
							}

							// Handle success
							if (isSuccess) {
								setTimeout(() => {
									setHideStepper(false);
									setPage(3);
								}, 3000);
							}

							setLiveness((prev) => ({ ...(prev as any), results: data }));
						} else {
							setLiveness(null);
							setFormError('Esta sesión de verificación biométrica ha expirado.');
						}
					} catch (error) {
						setLiveness(null);
						setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
						console.error('Error processing liveness snapshot:', error);
					}
				},
				(error) => {
					setLiveness(null);
					setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
					console.error('Error listening to liveness document:', error);
				});
		} catch (error) {
			console.error('Error setting up liveness document listener:', error);
		}

		return () => {
			try {
				if (unsub) unsub();
			} catch {}
		};
	}, [liveness?.documentId]);

	// Cambiar teléfono con 2FA
	const [applyLoading, setApplyLoading] = useState(false);
	const [applyErr, setApplyErr] = useState('');
	const applyChangePhoneNumber = async (newPhone: string) => {
		setApplyErr('');
		if (!userIdentificationNumber) {
			setApplyErr('Usuario no cargado.');
			return;
		}
		if (!validate_phone_number(newPhone?.trim() || '', userCountry || userData?.country || '')) {
			setApplyErr('Error: El número de teléfono no es válido.');
			return;
		}
		setApplyLoading(true);
		try {
			const resp: any = await unsafeAPI(
				'POST',
				toApiUrl('edit_phone_number/'),
				{
					identification_number: format_identification_with_prefix(userIdentificationNumber, CountrySettings?.uid_prefix || ''),
					incorrect_phone_number: currentPhoneNumber || '',
					new_phone_number: newPhone.trim()
				},
				userData,
				userCountry || userData?.country || ""
			);
			if (resp?.success === true) {
				setSuccess(true);
				setCurrentPhoneNumber(newPhone.trim());
			} else {
				setApplyErr(resp?.status?.length ? 'Error: ' + resp.status : 'Ocurrió un problema, intente de nuevo más tarde.');
			}
		} catch (err: any) {
			setApplyErr('Error: ' + (err?.message || 'Desconocido'));
		} finally {
			setApplyLoading(false);
		}
	};

	// Helper function to render header section
	const renderHeader = (title: string, description: string | null, icon: React.ReactElement | null) => {
		return (
			<div className="mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
				<div className="relative">
					<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
					<div className="relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">{icon && React.cloneElement(icon, { className: 'h-6 w-6 text-primary-500' })}</div>
				</div>
				<div className="space-y-1 text-center">
					<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
					{description && <p className="text-neutral-300">{description}</p>}
				</div>
			</div>
		);
	};

	if (!CountrySettings || !userData) {
		return <ContainerSpinner size={3} />;
	}

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			{!hideStepper && <Stepper currentPage={page} steps={steps} />}
			<div className="m-auto flex h-full w-full flex-col items-center justify-center p-4 md:p-6">
				<div className={classNames('w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8', { 'w-[800px]': page === 2 && (livenessMode !== 'idle' || hasCameraAvailable) })}>
					{page === 1 && (
						<>
							{renderHeader('Cambiar Teléfono', `Ingrese ${userData?.country === 'peru' ? 'el DNI' : 'la cédula'} del cliente para continuar`, <Search />)}
							<form className="space-y-4" onSubmit={handleSearchUser}>
								<Input name="cedula" floatingLabel={`${cedulaLabel} del cliente`} value={cedula} onChange={setCedula} placeholder={`Ingrese ${cedulaLabel} del cliente`} allowedChars="numbers_only" />
								{searchError && <Notice message={searchError} variant="error" />}
								<Button disabled={!cedula || searchLoading} loading={searchLoading} type="submit" variant="primary">Continuar</Button>
							</form>
						</>
					)}

					{page === 2 && (
						<>
							{renderHeader('Verificación Biométrica', 'Para continuar, verifique la identidad del cliente usando reconocimiento facial', <KeyRound />)}
							{livenessMode === 'idle' && (
								<div className="space-y-6">
									<div className="grid gap-4 md:gap-6 lg:grid-cols-2 lg:items-stretch">
										{/* Option 1: Continue on this device */}
										{hasCameraAvailable && (
											<div
												className={`group relative flex flex-col overflow-hidden rounded-xl border p-6 backdrop-blur-sm transition-all duration-300 ${
													!cameraTestInProgress && !isCameraFunctional
														? 'pointer-events-none border-red-500/30 bg-gradient-to-br from-red-950/20 to-neutral-950/40'
														: 'border-neutral-700 bg-gradient-to-br from-neutral-950/80 to-neutral-950/40 hover:border-primary-500/60 hover:shadow-xl hover:shadow-primary-500/20'
												}`}
											>
												{/* Subtle gradient overlay */}
												<div
													className={`absolute inset-0 bg-gradient-to-br via-transparent to-transparent opacity-0 transition-opacity duration-300 ${
														!cameraTestInProgress && !isCameraFunctional
															? 'from-red-500/5 group-hover:opacity-50'
															: 'from-primary-500/5 group-hover:opacity-100'
													}`}
												/>

												<div className="relative flex flex-1 flex-col">
													<div className="mb-6 flex items-start gap-4">
														<div
															className={`relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl backdrop-blur-sm ${
																!cameraTestInProgress && !isCameraFunctional
																	? 'bg-gradient-to-br from-red-500/20 to-red-600/10'
																	: 'bg-gradient-to-br from-primary-500/30 to-primary-600/20'
															}`}
														>
															<div
																className={`absolute inset-0 rounded-xl bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
																	!cameraTestInProgress && !isCameraFunctional
																		? 'from-red-400/20 to-red-600/10'
																		: 'from-primary-400/20 to-primary-600/10'
																}`}
															/>
															{cameraTestInProgress ? (
																<div className="flex space-x-1">
																	<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.3s] light:bg-primary-600" />
																	<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.15s] light:bg-primary-600" />
																	<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 light:bg-primary-600" />
																</div>
															) : !isCameraFunctional ? (
																<AlertTriangle className="relative h-6 w-6 text-red-400 transition-colors duration-300 light:text-red-600" />
															) : (
																<Camera className="relative h-6 w-6 text-primary-400 transition-colors duration-300 group-hover:text-primary-300 light:text-primary-600" />
															)}
														</div>
														<div className="flex-1 space-y-3">
															<h3
																className={`text-lg font-semibold transition-colors duration-300 ${
																	cameraTestInProgress
																		? 'text-neutral-0 group-hover:text-primary-100 light:group-hover:text-neutral-0'
																		: !isCameraFunctional
																			? 'text-red-400 light:text-red-600'
																			: 'text-neutral-0 group-hover:text-primary-100 light:group-hover:text-neutral-0'
																}`}
															>
																{cameraTestInProgress
																	? 'Verificando cámara...'
																	: !isCameraFunctional
																		? 'Cámara no disponible'
																		: 'Continuar en este dispositivo'}
															</h3>
															<p
																className={`text-sm leading-relaxed transition-colors duration-300 ${
																	cameraTestInProgress
																		? 'text-neutral-300 group-hover:text-neutral-200'
																		: !isCameraFunctional
																			? 'text-red-300 light:text-red-900'
																			: 'text-neutral-300 group-hover:text-neutral-200'
																}`}
															>
																{cameraTestInProgress
																	? 'Verificando si este dispositivo tiene cámara y funciona correctamente...'
																	: !isCameraFunctional
																		? 'La cámara está bloqueada por el sistema operativo o el navegador. Por favor, habilite los permisos de cámara en el sistema operativo y/o navegador, y recargue la página.'
																		: 'Use la cámara de este dispositivo para realizar la verificación facial de forma rápida y segura.'}
															</p>
														</div>
													</div>

													{/* Push button to bottom */}
													<div className="mt-auto">
														<Button
															disabled={isLoading || cameraTestInProgress || !isCameraFunctional || isLivenessRateLimited}
															variant="secondary"
															className="w-full border-neutral-600 bg-neutral-800/80 text-neutral-100 backdrop-blur-sm transition-all duration-300 hover:border-primary-500/60 hover:bg-primary-500/10 hover:text-primary-100 light:hover:text-primary-700"
															onClick={async () => {
																if (isCameraFunctional && !isLivenessRateLimited) {
																	const success = await initializeLiveness();
																	if (success) setLivenessMode('embedded');
																}
															}}
														>
															{!isCameraFunctional ? (
																<AlertTriangle className="mr-2 h-4 w-4 shrink-0" />
															) : (
																<Camera className="mr-2 h-4 w-4 shrink-0" />
															)}
															<span className="hidden sm:inline">
																{isLivenessRateLimited
																	? `Esperar ${livenessRateLimitTimer}s`
																	: cameraTestInProgress
																		? 'Verificando...'
																		: !isCameraFunctional
																			? 'Cámara no disponible'
																			: 'Usar cámara de este dispositivo'}
															</span>
															<span className="sm:hidden">
																{isLivenessRateLimited
																	? `Esperar ${livenessRateLimitTimer}s`
																	: cameraTestInProgress
																		? 'Verificando...'
																		: !isCameraFunctional
																			? 'No disponible'
																			: 'Usar este dispositivo'}
															</span>
														</Button>
													</div>
												</div>
											</div>
										)}

										{/* Option 2: QR code for mobile */}
										<div
											className={`group relative flex flex-col overflow-hidden rounded-xl border border-neutral-700 bg-gradient-to-br from-neutral-950/80 to-neutral-950/40 p-6 backdrop-blur-sm transition-all duration-300 hover:border-neutral-600 hover:shadow-xl hover:shadow-neutral-500/10 ${
												!hasCameraAvailable ? 'col-span-2' : ''
											}`}
										>
											{/* Subtle gradient overlay */}
											<div className="absolute inset-0 bg-gradient-to-br from-neutral-600/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

											<div className="relative flex flex-1 flex-col">
												<div className="mb-6 flex items-start gap-4">
													<div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-neutral-800/80 to-neutral-900/60 backdrop-blur-sm transition-all duration-300 group-hover:from-neutral-700/80 group-hover:to-neutral-800/60 light:bg-neutral-700/50">
														<QrCode className="h-6 w-6 text-neutral-400 transition-colors duration-300 group-hover:text-neutral-300" />
													</div>
													<div className="flex-1 space-y-3">
														<h3 className="text-lg font-semibold text-neutral-0 transition-colors duration-300 group-hover:text-neutral-100">
															Usar dispositivo móvil
														</h3>
														<p className="text-sm leading-relaxed text-neutral-300 transition-colors duration-300 group-hover:text-neutral-200">
															Genere un código QR para completar la verificación desde un teléfono móvil.
														</p>
														{hasCameraAvailable && (
															<div className="flex items-center gap-2 text-xs text-neutral-400 transition-colors duration-300 group-hover:text-neutral-300">
																<PhoneCall className="h-4 w-4 flex-shrink-0" />
																<span>Opción alternativa</span>
															</div>
														)}
													</div>
												</div>

												{/* Push button to bottom */}
												<div className="mt-auto">
													<Button
														disabled={isLoading || isLivenessRateLimited}
														variant="ghost"
														className="w-full border border-neutral-600 bg-neutral-800/60 text-neutral-200 backdrop-blur-sm transition-all duration-300 hover:border-neutral-500 hover:bg-neutral-700/80 hover:text-neutral-100 hover:shadow-lg"
														onClick={async () => {
															if (!isLivenessRateLimited) {
																const success = await initializeLiveness();
																if (success) setLivenessMode('qr');
															}
														}}
													>
														<QrCode className="mr-2 h-4 w-4 shrink-0" />
														{isLivenessRateLimited ? `Esperar ${livenessRateLimitTimer}s` : 'Generar código QR'}
													</Button>
												</div>
											</div>
										</div>
									</div>

									{formError && <Notice message={formError} variant="error" />}

									{/* Back button */}
									<div className="flex justify-center">
										<Button
											disabled={isLoading}
											Icon={<ArrowLeft className="size-5" />}
											type="button"
											onClick={() => setPage(1)}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>
								</div>
							)}

							{livenessMode === 'embedded' && (
								<div className="space-y-6">
									{/* Success State */}
									{liveness?.results?.valid_user === true &&
									liveness?.results?.user_authenticated === true &&
									liveness?.results?.photo_id_url ? (
										<div className="!mt-4 space-y-10">
											<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
												<div className="space-y-2 text-center">
													<div className="flex items-center justify-center gap-4">
														<div className="relative">
															<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
															<div className="relative rounded-full bg-neutral-900 p-4">
																<CircleCheck className="h-6 w-6 text-primary-500" />
															</div>
														</div>
														<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
													</div>
													<p className="text-neutral-300">La verificación biométrica se ha completado correctamente.</p>
												</div>
											</div>
										</div>
									) : (
										<>
											<LivenessDetection
												config={{
													isNewUser: false,
													userIdentificationIdUrl: '',
													livenessSessionId: liveness?.sessionId,
													livenessDocumentId: liveness?.documentId,
													userIdentificationNumber: userIdentificationNumber,
													embeddedMode: true,
													onReady: () => {
														setHideStepper(true);
													}
												}}
											/>
											<div className="flex justify-center">
												<Button
													disabled={
														liveness?.results?.valid_user === true &&
														liveness?.results?.user_authenticated === true &&
														liveness?.results?.photo_id_url
													}
													Icon={<ArrowLeft className="size-5" />}
													type="button"
													onClick={() => {
														setLiveness(null);
														setFormError('');
														setPage(1);
														setHideStepper(false);
														setLivenessMode('idle');
													}}
													variant="secondary"
												>
													Atrás
												</Button>
											</div>
										</>
									)}
								</div>
							)}

							{livenessMode === 'qr' && (
								<div className="space-y-6">
									<div className="flex flex-col items-center justify-center gap-6 min-[875px]:flex-row min-[875px]:items-start min-[875px]:gap-4">
										<div className="flex shrink-0 flex-col items-center justify-center gap-3">
											<div className="rounded-xl border border-neutral-700 bg-neutral-950/50 p-4">
												<QRCodeSVG
													value={liveness?.qrUrl || ''}
													bgColor="hsl(var(--twc-neutral-0))"
													fgColor="hsl(var(--twc-neutral-1000))"
													marginSize={2}
													className="size-[200px] rounded-lg"
												/>
											</div>
											<button
												onClick={async () => {
													try {
														if (liveness?.qrUrl) {
															copy(liveness.qrUrl);
															setIsCopySuccess(true);
															setTimeout(() => {
																setIsCopySuccess(false);
															}, 2000);
														}
													} catch (err) {
														console.error('Failed to copy URL:', err);
													}
												}}
												className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
													isCopySuccess
														? 'border-green-500/50 bg-green-500/20 text-green-400 hover:border-green-500/70 hover:bg-green-500/30'
														: 'border-neutral-600 bg-neutral-800/50 text-neutral-300 hover:border-neutral-500 hover:bg-neutral-700/50 hover:text-neutral-200'
												}`}
												title="Copiar enlace del QR"
											>
												{isCopySuccess ? (
													<>
														<CircleCheck className="size-4" />
														<span>¡Copiado!</span>
													</>
												) : (
													<>
														<Copy className="size-4" />
														<span>Copiar enlace</span>
													</>
												)}
											</button>
										</div>
										<div className="max-w-full grow space-y-6 min-[875px]:h-[233.6px]">
											<div className="relative min-[875px]:h-full">
												<div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-500/10 via-primary-500/5 to-transparent blur-xl min-[875px]:h-full" />
												<div className="relative overflow-hidden rounded-2xl border border-primary-500/20 bg-gradient-to-br from-neutral-900/80 to-neutral-950/90 p-6 backdrop-blur-sm md:p-8 min-[875px]:h-full">
													<div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/20 via-transparent to-primary-500/20 opacity-50 light:from-primary-500/10 light:to-primary-500/10">
														{liveness?.results?.processing &&
														!liveness?.results?.valid_user &&
														!liveness?.results?.user_authenticated && (
															<div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-primary-500/10 to-transparent" />
														)}
													</div>
													<div className="relative flex h-full min-h-[180px] flex-col min-[875px]:justify-center">
														{/* Success State */}
														{liveness?.results?.valid_user === true &&
														liveness?.results?.user_authenticated === true &&
														liveness?.results?.photo_id_url ? (
															<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
																<div className="space-y-4 text-center">
																	<div className="flex items-center justify-center gap-4">
																		<div className="relative">
																			<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																			<div className="relative rounded-full bg-neutral-900 p-4">
																				<CircleCheck className="h-6 w-6 text-primary-500" />
																			</div>
																		</div>
																		<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
																	</div>
																	<p className="text-neutral-300">
																		La verificación biométrica se ha completado correctamente desde el dispositivo móvil.
																	</p>
																</div>
															</div>
														) : (
															/* Waiting State */
															<>
																{/* Main content section */}
																<div className="space-y-4">
																	<div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:gap-6 sm:text-left">
																		<div className="relative">
																			{liveness?.results?.processing && (
																				<div className="absolute -inset-3 animate-pulse rounded-full bg-primary-500/20 blur" />
																			)}
																			<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/20 to-primary-600/20 backdrop-blur-sm light:from-primary-500/30 light:to-primary-600/30">
																				{liveness?.results?.processing ? (
																					<div className="flex space-x-1">
																						<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.3s] light:bg-primary-600" />
																						<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.15s] light:bg-primary-600" />
																						<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 light:bg-primary-600" />
																					</div>
																				) : (
																					<QrCode className="h-8 w-8 text-primary-400 light:text-primary-600" />
																				)}
																			</div>
																		</div>
																		<div className="flex-1 space-y-2">
																			<h3 className="text-lg font-semibold text-neutral-0 md:text-xl">
																				{liveness?.results?.processing
																					? 'Verificación biométrica en curso'
																					: 'Escanee el código QR'}
																			</h3>
																			<p className="text-sm leading-relaxed text-neutral-300 md:text-base min-[875px]:line-clamp-2">
																				{liveness?.results?.processing
																					? 'La verificación biométrica se está realizando. Por favor espere...'
																					: 'Escanee el código QR con su dispositivo móvil para iniciar la verificación'}
																			</p>
																		</div>
																	</div>

																	{/* Show error message if there was an attempt but it failed */}
																	{(liveness?.results?.valid_user === false ||
																		liveness?.results?.user_authenticated === false ||
																		(liveness?.results?.valid_user === true &&
																			liveness?.results?.user_authenticated === true &&
																			!liveness?.results?.photo_id_url)) && (
																		<div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 animate-in fade-in-50 slide-in-from-top-2">
																			<div className="flex items-center gap-2">
																				<AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-400" />
																				<div>
																					<p className="text-sm font-medium text-red-400 light:text-red-500">
																						Verificación Fallida
																					</p>
																					<p className="text-xs text-red-300/80 light:text-red-500/70">
																						La verificación falló. Vuelva a intentarlo desde el móvil.
																					</p>
																				</div>
																			</div>
																		</div>
																	)}
																</div>

																{/* Step indicators - positioned at bottom */}
																{!(liveness?.results?.valid_user === false || liveness?.results?.user_authenticated === false) && (
																	<div className="mt-auto flex flex-wrap items-center justify-center gap-2 pt-4 sm:flex-nowrap sm:justify-start">
																		<div
																			className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
																				!liveness?.results?.processing
																					? 'bg-orange-500/20 text-orange-400'
																					: 'bg-neutral-800/50 text-neutral-300'
																			}`}
																		>
																			<div
																				className={`size-2 rounded-full ${
																					!liveness?.results?.processing ? 'animate-pulse bg-orange-400' : 'bg-green-400'
																				}`}
																			/>
																			<span>Paso 1: Escanear QR</span>
																		</div>
																		<div
																			className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
																				liveness?.results?.processing
																					? 'bg-orange-500/20 text-orange-400'
																					: 'bg-neutral-800/30 text-neutral-400'
																			}`}
																		>
																			<div
																				className={`size-2 rounded-full ${
																					liveness?.results?.processing ? 'animate-pulse bg-orange-400' : 'bg-neutral-600'
																				}`}
																			/>
																			<span>Paso 2: Verificar rostro</span>
																		</div>
																	</div>
																)}
															</>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className="flex justify-center">
										<Button
											disabled={
												liveness?.results?.valid_user === true &&
												liveness?.results?.user_authenticated === true &&
												liveness?.results?.photo_id_url
											}
											Icon={<ArrowLeft className="size-5" />}
											type="button"
											onClick={() => {
												setLiveness(null);
												setFormError('');
												setPage(1);
												setHideStepper(false);
												setLivenessMode('idle');
											}}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>
								</div>
							)}
						</>
					)}

					{page === 3 && (
						<>
							{renderHeader('Cambiar Teléfono', 'Ingrese el nuevo número y valide con un código de verificación', <PhoneCall />)}
							<div className="space-y-4">
								<div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-3">
									<div className="flex items-center justify-between">
										<span className="text-sm text-neutral-400">Teléfono actual</span>
										<span className="text-sm text-neutral-100">{currentPhoneNumber || '—'}</span>
									</div>
								</div>
								<TwoFactorAuth country={userCountry || userData?.country || ''} select_phone_numbers={undefined} onSuccess={async (phone_number) => { await applyChangePhoneNumber(phone_number); }} />
								{applyErr && <Notice message={applyErr} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled variant="primary" loading={applyLoading}>Guardar</Button>
									<Button Icon={<ArrowLeft className="size-5" />} disabled={applyLoading} type="button" onClick={() => setPage(2)} variant="secondary">Atrás</Button>
								</div>
							</div>
						</>
					)}

					{page === 4 && (
						<>
							{renderHeader('Teléfono Actualizado', '¡El número de teléfono se ha actualizado correctamente!', <CircleCheck />)}

							<div className="flex gap-2">
								<Button
									Icon={<ArrowLeft className="size-5" />}
									onClick={() => {
										setSuccess(false);
										setApplyErr('');
										setCedula('');
										setUserIdentificationNumber('');
										setUserCountry('');
										setCurrentPhoneNumber('');
										setLiveness(null);
										setLivenessMode('idle');
										setFormError('');
										setPage(1);
									}}
									variant="primary"
								>
									Cambiar otro teléfono
								</Button>
								<Button
									onClick={() => {
			
										window.location.href = '/';
									}}
									variant="secondary"
								>
									Volver al inicio
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default CambiarTelefono;
